import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  EnvelopeIcon,
  PhoneIcon,
  AcademicCapIcon,
  ClockIcon,
  UserGroupIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/solid';
import { useConfirm } from '../ConfirmDialog';

export default function TutoringRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [request, setRequest] = useState(null);
  const [suggestedTutors, setSuggestedTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTutors, setLoadingTutors] = useState(false);
  const [matchingTutor, setMatchingTutor] = useState(false);
  const [creatingClassroom, setCreatingClassroom] = useState(false);
  const [showClassroomModal, setShowClassroomModal] = useState(false);
  const [classroomName, setClassroomName] = useState('');
  const [classroomDescription, setClassroomDescription] = useState('');
  const [showManualMatch, setShowManualMatch] = useState(false);
  const [allTutors, setAllTutors] = useState([]);
  const [loadingAllTutors, setLoadingAllTutors] = useState(false);
  const [selectedManualTutor, setSelectedManualTutor] = useState('');

  useEffect(() => {
    fetchRequest();
  }, [id]);

  const fetchRequest = async () => {
    try {
      const { data } = await axios.get('/api/admin/tutoring-requests');
      // Backend returns { requests: [...] }
      const requestsArray = data.requests || data || [];
      const foundRequest = requestsArray.find(r => r.id === id);

      if (!foundRequest) {
        toast.error('Request not found');
        navigate('/admin/requests');
        return;
      }

      setRequest(foundRequest);

      // Set default classroom name
      setClassroomName(`${foundRequest.subject} - ${foundRequest.name}`);
      setClassroomDescription(`${foundRequest.subject} tutoring for ${foundRequest.grade_level}`);

      // Fetch suggested tutors if not matched yet
      if (foundRequest.status === 'new' || foundRequest.status === 'reviewing') {
        fetchSuggestedTutors(foundRequest.id);
      }
    } catch (error) {
      console.error('Error fetching request:', error);
      toast.error('Failed to load request details');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestedTutors = async (requestId) => {
    setLoadingTutors(true);
    try {
      const { data } = await axios.get(`/api/admin/suggest-tutors/${requestId}`);
      setSuggestedTutors(data.suggested_tutors || []);
    } catch (error) {
      console.error('Error fetching suggested tutors:', error);
      toast.error('Failed to load suggested tutors');
      setSuggestedTutors([]);
    } finally {
      setLoadingTutors(false);
    }
  };

  const fetchAllTutors = async () => {
    setLoadingAllTutors(true);
    try {
      const { data } = await axios.get('/api/admin/tutors');
      setAllTutors(data.tutors || []);
      setShowManualMatch(true);
    } catch (error) {
      console.error('Error fetching all tutors:', error);
      toast.error('Failed to load tutors');
    } finally {
      setLoadingAllTutors(false);
    }
  };

  const handleManualMatch = async () => {
    if (!selectedManualTutor) {
      toast.error('Please select a tutor');
      return;
    }
    await handleMatchTutor(selectedManualTutor);
    setShowManualMatch(false);
    setSelectedManualTutor('');
  };

  const handleMatchTutor = async (tutorId) => {
    if (!(await confirm({
      title: 'Match this tutor?',
      message: 'The tutor will be matched to the student and an email will be sent to the tutor.',
      confirmLabel: 'Match tutor',
      danger: false,
    }))) {
      return;
    }

    setMatchingTutor(true);
    try {
      const response = await axios.put('/api/admin/match-tutor', {
        request_id: id,
        tutor_id: tutorId
      });

      // Check if email was sent successfully
      if (response.data.emailSent) {
        toast.success('Tutor matched successfully and notification email sent!');
      } else {
        toast.success('Tutor matched successfully!', { duration: 3000 });
        toast.error(`Warning: Notification email failed - ${response.data.emailError || 'Unknown error'}`, {
          duration: 6000
        });
      }

      fetchRequest(); // Refresh to show updated status
    } catch (error) {
      console.error('Error matching tutor:', error);
      toast.error(error.response?.data?.error || 'Failed to match tutor');
    } finally {
      setMatchingTutor(false);
    }
  };

  const handleCreateClassroom = async (e) => {
    e.preventDefault();
    setCreatingClassroom(true);

    try {
      const response = await axios.put('/api/admin/create-classroom-from-request', {
        request_id: id,
        classroom_name: classroomName,
        classroom_description: classroomDescription
      });

      console.log('Classroom created:', response.data);
      toast.success('Classroom created and student account set up!');
      setShowClassroomModal(false);
      fetchRequest(); // Refresh to show updated status
    } catch (error) {
      console.error('Error creating classroom:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.error || 'Failed to create classroom');
    } finally {
      setCreatingClassroom(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      reviewing: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      matched: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      classroom_created: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${styles[status] || styles.new}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!request) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/requests')}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          Back to Requests
        </button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {request.name}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Requested {new Date(request.created_at).toLocaleDateString()}
            </p>
          </div>
          {getStatusBadge(request.status)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Parent/Guardian Contact - Show prominently if parent is primary contact */}
          {request.parent_will_contact && (
            <div className="bg-amber-50 dark:bg-amber-900/30 rounded-lg shadow-md p-6 border-2 border-amber-400 dark:border-amber-600 mb-6">
              <h2 className="text-xl font-bold text-amber-800 dark:text-amber-200 mb-4 flex items-center gap-2">
                <UserGroupIcon className="h-6 w-6" />
                Primary Contact: Parent/Guardian
              </h2>
              <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
                A parent/guardian is handling communications for this student. Contact them for scheduling and updates.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {request.parent_name && (
                  <div className="min-w-0">
                    <div className="text-sm text-amber-600 dark:text-amber-400 mb-1">Name</div>
                    <div className="font-semibold text-gray-900 dark:text-white truncate">
                      {request.parent_name}
                    </div>
                  </div>
                )}

                {request.parent_email && (
                  <div className="min-w-0">
                    <div className="text-sm text-amber-600 dark:text-amber-400 mb-1">Email</div>
                    <div className="flex items-center gap-2 text-gray-900 dark:text-white min-w-0">
                      <EnvelopeIcon className="h-5 w-5 text-amber-500 flex-shrink-0" />
                      <a href={`mailto:${request.parent_email}`} className="hover:text-blue-500 font-semibold truncate">
                        {request.parent_email}
                      </a>
                    </div>
                  </div>
                )}

                {request.parent_phone && (
                  <div className="min-w-0">
                    <div className="text-sm text-amber-600 dark:text-amber-400 mb-1">Phone</div>
                    <div className="flex items-center gap-2 text-gray-900 dark:text-white min-w-0">
                      <PhoneIcon className="h-5 w-5 text-amber-500 flex-shrink-0" />
                      <a href={`tel:${request.parent_phone}`} className="hover:text-blue-500 font-semibold truncate">
                        {request.parent_phone}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Contact & Request Details */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-transparent dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              {request.parent_will_contact ? 'Student Details' : 'Request Details'}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Student Email</div>
                <div className="flex items-center gap-2 text-gray-900 dark:text-white">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                  <a href={`mailto:${request.email}`} className="hover:text-blue-500">
                    {request.email}
                  </a>
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Student Phone</div>
                <div className="flex items-center gap-2 text-gray-900 dark:text-white">
                  <PhoneIcon className="h-5 w-5 text-gray-400" />
                  <a href={`tel:${request.phone}`} className="hover:text-blue-500">
                    {request.phone}
                  </a>
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Subject</div>
                <div className="flex items-center gap-2 font-semibold text-gray-900 dark:text-white">
                  <AcademicCapIcon className="h-5 w-5 text-gray-400" />
                  {request.subject}
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Grade Level</div>
                <div className="font-semibold text-gray-900 dark:text-white">
                  {request.grade_level}
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Sessions Needed</div>
                <div className="font-semibold text-gray-900 dark:text-white">
                  {request.sessions_needed || 'Not specified'}
                </div>
              </div>
            </div>

            {request.preferred_times && request.preferred_times.length > 0 && (
              <div className="mt-6">
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">Preferred Times</div>
                <div className="flex flex-wrap gap-2">
                  {request.preferred_times.map((time, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm"
                    >
                      {time}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {request.additional_notes && (
              <div className="mt-6">
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">Additional Notes</div>
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                  {request.additional_notes}
                </div>
              </div>
            )}
          </div>

          {/* Suggested Tutors */}
          {(request.status === 'new' || request.status === 'reviewing') && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-transparent dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Suggested Tutors
              </h2>

              {loadingTutors ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                </div>
              ) : suggestedTutors.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    No tutors available for this subject and grade level
                  </p>
                  {!showManualMatch ? (
                    <button
                      onClick={fetchAllTutors}
                      disabled={loadingAllTutors}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                    >
                      {loadingAllTutors ? 'Loading...' : 'Manual Match from All Tutors'}
                    </button>
                  ) : (
                    <div className="max-w-md mx-auto text-left">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Select a Tutor
                      </label>
                      <select
                        value={selectedManualTutor}
                        onChange={(e) => setSelectedManualTutor(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white mb-3"
                      >
                        <option value="">-- Select a tutor --</option>
                        {allTutors.map((tutor) => (
                          <option key={tutor.id} value={tutor.id}>
                            {tutor.name} ({tutor.email}) - {tutor.studentsCount} students
                          </option>
                        ))}
                      </select>
                      <div className="flex gap-2">
                        <button
                          onClick={handleManualMatch}
                          disabled={!selectedManualTutor || matchingTutor}
                          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                        >
                          {matchingTutor ? 'Matching...' : 'Match Selected Tutor'}
                        </button>
                        <button
                          onClick={() => {
                            setShowManualMatch(false);
                            setSelectedManualTutor('');
                          }}
                          className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                  {suggestedTutors.map((tutor) => (
                    <div
                      key={tutor.tutor_id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-blue-500 transition"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-gray-900 dark:text-white">
                            {tutor.tutor_name}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {tutor.tutor_email}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                            {tutor.match_score}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Match Score
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                        <div>
                          <div className="text-gray-500 dark:text-gray-400">Students</div>
                          <div className="font-semibold text-gray-900 dark:text-white">
                            {tutor.current_students}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500 dark:text-gray-400">Experience</div>
                          <div className="font-semibold text-gray-900 dark:text-white">
                            {tutor.years_experience || 'Not specified'}
                          </div>
                        </div>
                      </div>

                      <div className="mb-4">
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Can Teach</div>
                        <div className="flex flex-wrap gap-1">
                          {(tutor.subjects || []).map((subject, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs"
                            >
                              {subject}
                            </span>
                          ))}
                        </div>
                      </div>

                      <button
                        onClick={() => handleMatchTutor(tutor.tutor_id)}
                        disabled={matchingTutor}
                        className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {matchingTutor ? 'Matching...' : 'Match This Tutor'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar Actions */}
        <div className="space-y-6">
          {/* Matched Tutor Info */}
          {request.matched_tutor_id && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-transparent dark:border-gray-700">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
                Matched Tutor
              </h3>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Tutor has been notified via email
              </div>
              {request.matched_at && (
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Matched on {new Date(request.matched_at).toLocaleDateString()}
                </div>
              )}
            </div>
          )}

          {/* Create Classroom */}
          {request.status === 'matched' && !request.classroom_id && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-transparent dark:border-gray-700">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4">
                Next Step
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Tutor has been matched. Create a classroom to give the student access to Solomon.
              </p>
              <button
                onClick={() => {
                  console.log('Create Classroom button clicked');
                  setShowClassroomModal(true);
                }}
                className="w-full bg-green-500 text-white py-3 rounded-lg hover:bg-green-600 transition font-semibold"
              >
                Create Classroom
              </button>
            </div>
          )}

          {/* Debug info - show when button is not visible due to unexpected status */}
          {request.matched_tutor_id && !request.classroom_id && request.status !== 'matched' && request.status !== 'new' && (
            <div className="bg-yellow-50 dark:bg-yellow-900 rounded-lg shadow-md p-6 border-2 border-yellow-400">
              <h3 className="font-bold text-yellow-900 dark:text-yellow-100 mb-2">
                ⚠️ Status Issue Detected
              </h3>
              <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-2">
                Tutor is matched but status is: <strong>{request.status}</strong>
              </p>
              <p className="text-xs text-yellow-700 dark:text-yellow-300">
                Expected status: "matched". This may prevent the Create Classroom button from showing.
              </p>
            </div>
          )}

          {/* Classroom Created */}
          {request.classroom_id && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-transparent dark:border-gray-700">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
                Classroom Created
              </h3>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Student account created and enrolled in classroom. They have received login instructions via email.
              </div>
              <button
                onClick={() => navigate(`/classroom/${request.classroom_id}`)}
                className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition"
              >
                View Classroom
              </button>
            </div>
          )}

          {/* Quick Actions */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-transparent dark:border-gray-700">
            <h3 className="font-bold text-gray-900 dark:text-white mb-4">
              Quick Actions
            </h3>
            <div className="space-y-2">
              {/* Parent contact buttons - show first if parent is primary contact */}
              {request.parent_will_contact && (
                <>
                  {request.parent_email && (
                    <a
                      href={`mailto:${request.parent_email}`}
                      className="block w-full text-center bg-amber-100 dark:bg-amber-900 text-amber-900 dark:text-amber-100 py-2 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-800 transition font-semibold"
                    >
                      Email Parent/Guardian
                    </a>
                  )}
                  {request.parent_phone && (
                    <a
                      href={`tel:${request.parent_phone}`}
                      className="block w-full text-center bg-amber-100 dark:bg-amber-900 text-amber-900 dark:text-amber-100 py-2 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-800 transition font-semibold"
                    >
                      Call Parent/Guardian
                    </a>
                  )}
                  <div className="border-t border-gray-200 dark:border-gray-600 my-2"></div>
                </>
              )}
              <a
                href={`mailto:${request.email}`}
                className="block w-full text-center bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white py-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition"
              >
                Email Student
              </a>
              <a
                href={`tel:${request.phone}`}
                className="block w-full text-center bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white py-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition"
              >
                Call Student
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Create Classroom Modal */}
      {showClassroomModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Create Classroom
            </h2>

            <form onSubmit={handleCreateClassroom} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Classroom Name
                </label>
                <input
                  type="text"
                  value={classroomName}
                  onChange={(e) => setClassroomName(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={classroomDescription}
                  onChange={(e) => setClassroomDescription(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg text-sm text-blue-900 dark:text-blue-100">
                This will:
                <ul className="list-disc ml-5 mt-2 space-y-1">
                  <li>Create a student account for {request.name}</li>
                  <li>Create a new classroom</li>
                  <li>Enroll the student</li>
                  <li>Send login instructions via email</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowClassroomModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingClassroom}
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingClassroom ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
